import { gql, useQuery } from '@apollo/client'
import _ from 'lodash'
import { useMemo } from 'react'

import { useDateFilter } from '../../../../dateFilter'
import { useGroupFilter } from '../../../../groupFilter'
import { IApiType } from '../../../types'

const QUERY_NAME = 'listLocationMetricValues'
const query = gql`
  query (
    $iStartDate: Date!
    $iEndDate: Date!
    $iFilter: JSON!
  ) {
    ${QUERY_NAME}(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) {
      nodes {
        locationId
        metricData
      }
    }
  }
`

export const fwWingstopLocationDayPartConfigs = {
  name: 'string',
  sales: 'price',
  percentSales: 'percent',
} as const

const useLocationDayPart = (): IApiType => {
  const { startDate, endDate } = useDateFilter()
  const { groupFilter } = useGroupFilter()

  const iFilter = {
    location_ids: groupFilter?.ids,
    metrics: [
      'net_sales',
      'sales_by_day_part_dinner',
      'sales_by_day_part_late_night',
      'sales_by_day_part_lunch',
      'sales_by_day_part_snack',
    ],
  }

  const { data, loading } = useQuery(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iFilter,
    },
    skip: !startDate || !endDate || !groupFilter,
  })

  return {
    data: useMemo(() => {
      const nodes = data?.[QUERY_NAME].nodes as
        | {
            locationId: string
            metricData: {
              [key: string]: {
                name: string
                unit: string
                value: number
              }
            }
          }[]
        | undefined

      if (!nodes) return null
      const mappedData: {
        [dayPart: string]: {
          [kpi: string]: number | string
        }
      } = {}
      const metricData = nodes[0]?.metricData
      let netSales = 0

      for (const key in metricData) {
        if (key === 'net_sales') {
          if (metricData[key].unit === 'CENT') {
            netSales = metricData[key].value / 100
          } else {
            netSales = metricData[key].value
          }
          continue
        }

        let kpi = ''
        let dayPart = ''
        let dayPartSales = 0
        if (key.indexOf('sales_by_day_part_') === 0) {
          if (metricData[key].unit === 'CENT') {
            dayPartSales = metricData[key].value / 100
          } else {
            dayPartSales = metricData[key].value
          }

          kpi = 'sales'
          dayPart = (metricData[key].name || '').replace('sales for ', '')
        }

        if (dayPart && kpi) {
          if (!mappedData[dayPart]) {
            mappedData[dayPart] = {
              name: dayPart,
              [kpi]: dayPartSales,
            }
          } else {
            mappedData[dayPart][kpi] = metricData[key].value
          }
        }
      }

      const rows = Object.values(mappedData)
        ?.filter((n) => n.sales)
        ?.map((n) => {
          return {
            ...n,
            percentSales: (100 * (n.sales as number)) / netSales,
            id: n.name as string,
            parentId: 'root',
          }
        })

      return rows
    }, [groupFilter, data]),
    loading,
  }
}

export default useLocationDayPart
